import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import Button from '../components/UI/Button';
import ActionButton from '../components/UI/ActionButton';
import { formatDateDMY, formatDateTimeDMY } from '../utils/dateUtils';
import { formatBytes } from '../utils/formatBytes';
import { Bell } from 'lucide-react';
import StatusBadge from '../components/UI/StatusBadge';
import Pager from '../components/UI/Pager';
import DisplayField from '../components/UI/DisplayField';

const todayIso = () => {
  // YYYY-MM-DD in local time (safe for <input type="date">)
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const SupportTickets = () => {
  const todayMax = todayIso();
  const [tickets, setTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limitStatus, setLimitStatus] = useState(null);

  const [page, setPage] = useState(1);
  const pageSize = 5;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const selectedTicket = tickets.find(t => Number(t.id) === Number(selectedTicketId)) || null;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [resendingTicketId, setResendingTicketId] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);

  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [techniciansLoadError, setTechniciansLoadError] = useState('');
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendTicketId, setResendTicketId] = useState(null);
  const [resendTechnicianId, setResendTechnicianId] = useState('');

  const [kind, setKind] = useState('SUPPORT');
  const [priority, setPriority] = useState('MEDIUM');
  const [topics, setTopics] = useState([]);
  const [topicId, setTopicId] = useState('');
  const [slaLevels, setSlaLevels] = useState([]);
  const [slaLevelId, setSlaLevelId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');

  const fetchLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      const limitsRes = await axios.get(apiUrl('/api/tickets/limit-status'), { headers: { Authorization: `Bearer ${token}` } });
      setLimitStatus(limitsRes.data || null);
    } catch {
      // ignore
    }
  };

  const fetchTickets = async (opts = {}) => {
    const pageOverride = Number(opts.pageOverride);
    const effectivePage = Number.isFinite(pageOverride) && pageOverride > 0 ? pageOverride : page;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const params = {
        page: effectivePage,
        pageSize,
      };
      const q = String(search || '').trim();
      if (q) params.q = q;
      const st = String(statusFilter || '').trim();
      if (st) params.status = st;

      const df = String(reportDateFrom || '').trim();
      const dt = String(reportDateTo || '').trim();
      if (df) params.dateFrom = df;
      if (dt) params.dateTo = dt;

      const ticketsRes = await axios.get(apiUrl('/api/tickets'), {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (Array.isArray(ticketsRes.data)) {
        setTickets(ticketsRes.data);
        setTicketsTotal(ticketsRes.data.length);
      } else {
        setTickets(Array.isArray(ticketsRes.data?.items) ? ticketsRes.data.items : []);
        setTicketsTotal(Number(ticketsRes.data?.total || 0));
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load tickets');
      setTickets([]);
      setTicketsTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAll = async () => {
    await Promise.all([fetchTickets(), fetchLimits()]);
  };

  const fetchTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(apiUrl('/api/tickets/topics'), { headers: { Authorization: `Bearer ${token}` } });
      setTopics(Array.isArray(r.data) ? r.data : []);
    } catch {
      setTopics([]);
    }
  };

  const fetchSlaLevels = async (topicIdRaw) => {
    const tid = Number(topicIdRaw);
    if (!tid) {
      setSlaLevels([]);
      setSlaLevelId('');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(apiUrl(`/api/tickets/topics/${tid}/sla-levels`), { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(r.data) ? r.data : [];
      setSlaLevels(list);
      const def = list.find((x) => x?.is_default) || list[0];
      setSlaLevelId(def?.id ? String(def.id) : '');
      const p = String(def?.priority || '').toUpperCase();
      if (p) setPriority(p);
    } catch {
      setSlaLevels([]);
      setSlaLevelId('');
    }
  };

  const fetchTechnicians = async () => {
    setTechniciansLoading(true);
    setTechniciansLoadError('');
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(apiUrl('/api/tickets/technicians'), { headers: { Authorization: `Bearer ${token}` } });
      setTechnicians(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setTechnicians([]);
      setTechniciansLoadError(e?.response?.data?.error || e?.message || 'Failed to load technicians');
    } finally {
      setTechniciansLoading(false);
    }
  };

  const loadMessages = async (ticketId) => {
    if (!ticketId) return;
    setMessagesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/tickets/${ticketId}/messages`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTicketMessages(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTicketMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadStatusHistory = async (ticketId) => {
    if (!ticketId) return;
    setStatusHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/tickets/${ticketId}/status-changes`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setStatusHistory([]);
    } finally {
      setStatusHistoryLoading(false);
    }
  };

  const loadAttachments = async (ticketId) => {
    if (!ticketId) return;
    setAttachmentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/tickets/${ticketId}/attachments`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTicketAttachments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTicketAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const viewAttachment = async (attachmentId, filename) => {
    if (!selectedTicket?.id || !attachmentId) return;

    // Open a tab synchronously to avoid popup blockers, then navigate when blob is ready.
    const newTab = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        apiUrl(`/api/tickets/${selectedTicket.id}/attachments/${attachmentId}/download`),
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);

      if (newTab && !newTab.closed) {
        newTab.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.textContent = 'View attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      window.URL.revokeObjectURL(url);
    } catch {
      if (newTab && !newTab.closed) newTab.close();
      setError('Failed to view attachment');
    }
  };

  const onSelectTicket = async (ticket) => {
    setSelectedTicketId(ticket?.id || null);
    setNewComment('');
    setIsModalOpen(true);
    await Promise.all([
      loadMessages(ticket?.id),
      loadAttachments(ticket?.id),
      loadStatusHistory(ticket?.id),
    ]);

    // Mark as read (clears highlight / unread bubble)
    try {
      const token = localStorage.getItem('token');
      await axios.post(apiUrl(`/api/tickets/${ticket?.id}/mark-read`), null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets((prev) => prev.map((t) => (
        Number(t.id) === Number(ticket?.id)
          ? { ...t, has_unread_support_messages: false, has_unread_status_change: false }
          : t
      )));
      window.dispatchEvent(new Event('tickets:unread-changed'));
    } catch {
      // ignore
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onPostComment = async () => {
    if (!selectedTicket?.id) return;
    const text = String(newComment || '').trim();
    if (!text) return;

    setPostingComment(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/tickets/${selectedTicket.id}/messages`),
        { message: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment('');
      await loadMessages(selectedTicket.id);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to add comment');
    } finally {
      setPostingComment(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchTechnicians();
    fetchTopics();
  }, []);

  useEffect(() => {
    if (topicId) fetchSlaLevels(topicId);
    else {
      setSlaLevels([]);
      setSlaLevelId('');
    }
  }, [topicId]);

  // SLA is decided by topic default; keep it read-only.

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchTickets({ pageOverride: 1 });
    }, 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, reportDateFrom, reportDateTo]);

  useEffect(() => {
    fetchTickets();
  }, [page]);

  const totalPages = Math.max(1, Math.ceil((ticketsTotal || 0) / pageSize));

  const InfoChip = ({ label, value, tone = 'gray' }) => {
    if (!value) return null;
    const styles = {
      gray: 'bg-gray-50 dark:bg-slate-700/50 text-gray-800 dark:text-slate-300 border-gray-200 dark:border-slate-600',
      blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-700/50',
      amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-300 border-amber-200 dark:border-amber-700/50',
      purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-300 border-purple-200 dark:border-purple-700/50',
      green: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/50',
      red: 'bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-300 border-red-200 dark:border-red-700/50',
    };
    const cls = styles[tone] || styles.gray;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-full border ${cls}`}>
        <span className="opacity-70">{label}:</span>
        <span className="truncate">{value}</span>
      </span>
    );
  };

  const priorityTone = (p) => {
    const v = String(p || '').toUpperCase();
    if (v === 'HIGH') return 'red';
    if (v === 'LOW') return 'gray';
    return 'amber';
  };

  const slaChip = ({ dueAt, violatedAt, overdue }) => {
    if (violatedAt) {
      return {
        value: `Violated ${formatDateTimeDMY(violatedAt)}`,
        tone: 'red',
      };
    }
    if (!dueAt) return null;
    return {
      value: `Due ${formatDateTimeDMY(dueAt)}`,
      tone: overdue ? 'red' : 'gray',
    };
  };

  const openResendModal = (ticketIdRaw) => {
    const ticketId = Number(ticketIdRaw || selectedTicket?.id);
    if (!ticketId) return;
    setError('');
    setMessage('');
    setResendTicketId(ticketId);
    setResendTechnicianId('');
    // Refresh list when opening to avoid an empty dropdown if initial page load happened before auth token existed.
    fetchTechnicians();
    setShowResendModal(true);
  };

  const onConfirmResend = async () => {
    const ticketId = Number(resendTicketId);
    if (!ticketId) return;
    const technicianId = resendTechnicianId ? Number(resendTechnicianId) : null;
    if (!technicianId) {
      setError('Please select a technician.');
      return;
    }

    setError('');
    setMessage('');
    try {
      setResendingTicketId(ticketId);
      const token = localStorage.getItem('token');
      const payload = { technicianId };
      const resp = await axios.post(apiUrl(`/api/tickets/${ticketId}/resend-email`), payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sentTo = resp?.data?.sentTo;
      if (sentTo?.email) setMessage(`Support link resent to selected technician: ${sentTo.name || 'technician'} (${sentTo.email}).`);
      else setMessage('Support link resent.');
      setShowResendModal(false);
      setResendTicketId(null);
      setResendTechnicianId('');
      fetchTickets();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to resend email');
    } finally {
      setResendingTicketId((prev) => (Number(prev) === Number(ticketId) ? null : prev));
    }
  };

  const onResendEmailForTicket = async (ticketIdRaw) => {
    openResendModal(ticketIdRaw);
  };

  const exportPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      const q = String(search || '').trim();
      if (q) params.q = q;
      const st = String(statusFilter || '').trim();
      if (st) params.status = st;

      const df = String(reportDateFrom || '').trim();
      const dt = String(reportDateTo || '').trim();
      if (dt && dt > todayMax) {
        setError('Date to cannot be in the future.');
        return;
      }
      if (df) params.dateFrom = df;
      if (dt) params.dateTo = dt;

      const res = await axios.get(apiUrl('/api/tickets/report/pdf'), {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tickets-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to export PDF');
    }
  };

  const canCreate = () => {
    if (!limitStatus) return true;
    const reached = limitStatus?.reached?.[kind];
    return !reached;
  };

  const renderLimitsCards = () => {
    if (!limitStatus) return null;

    const maxSupport = limitStatus?.maxSupportTicketsPerMonth;
    const usedSupport = limitStatus?.counts?.SUPPORT ?? 0;
    const remainingSupport = limitStatus?.remaining?.SUPPORT;

    const maxEnh = limitStatus?.maxOnceOffEnhancements;
    const usedEnh = limitStatus?.counts?.ENHANCEMENT ?? 0;
    const remainingEnh = limitStatus?.remaining?.ENHANCEMENT;

    const card = (title, value, className, subtitle) => (
      <div className={`p-3.5 rounded-xl border shadow-sm ${className}`}>
        <div className="text-[11px] font-semibold uppercase tracking-wide opacity-75">{title}</div>
        <div className="text-2xl font-bold mt-1">{value}</div>
        {subtitle ? <div className="text-xs mt-1 opacity-70 font-medium">{subtitle}</div> : null}
      </div>
    );

    const remainingClass = (remaining, max) => {
      const m = Number(max);
      if (!m || !Number.isFinite(m) || m <= 0) return 'bg-emerald-50 border-emerald-200 text-emerald-950';
      const r = Number(remaining);
      if (!Number.isFinite(r) || r <= 0) return 'bg-red-50 border-red-200 text-red-900';
      const ratio = r / m;
      if (r <= 1 || ratio <= 0.2) return 'bg-amber-50 border-amber-200 text-amber-950';
      return 'bg-emerald-50 border-emerald-200 text-emerald-950';
    };

    const usedClass = (used, max, base) => {
      const m = Number(max);
      const u = Number(used);
      if (m && Number.isFinite(m) && Number.isFinite(u) && u >= m) return 'bg-red-50 border-red-200 text-red-900';
      return base;
    };

    const supportMaxLabel = maxSupport ? String(maxSupport) : 'Unlimited';
    const supportRemainingLabel = maxSupport ? String(remainingSupport ?? 0) : 'Unlimited';
    const enhMaxLabel = maxEnh ? String(maxEnh) : 'Unlimited';
    const enhRemainingLabel = maxEnh ? String(remainingEnh ?? 0) : 'Unlimited';

    const supportReached = !!limitStatus?.reached?.SUPPORT;
    const enhReached = !!limitStatus?.reached?.ENHANCEMENT;

    return (
      <div className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {card('Support max (this month)', supportMaxLabel, 'bg-blue-50 border-blue-200 text-blue-900')}
          {card('Support used (this month)', String(usedSupport), usedClass(usedSupport, maxSupport, 'bg-indigo-50 border-indigo-200 text-indigo-900'), supportReached ? 'Limit reached' : undefined)}
          {card('Support remaining (this month)', supportRemainingLabel, remainingClass(remainingSupport, maxSupport), supportReached ? 'Limit reached' : undefined)}

          {card('Enhancement max (once-off)', enhMaxLabel, 'bg-purple-50 border-purple-200 text-purple-900')}
          {card('Enhancement used (once-off)', String(usedEnh), usedClass(usedEnh, maxEnh, 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900'), enhReached ? 'Limit reached' : undefined)}
          {card('Enhancement remaining (once-off)', enhRemainingLabel, remainingClass(remainingEnh, maxEnh), enhReached ? 'Limit reached' : undefined)}
        </div>
      </div>
    );
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required.');
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('kind', kind);
      if (topicId) formData.append('topicId', String(topicId));
      formData.append('subject', subject.trim());
      formData.append('description', description.trim());
      for (const f of attachments) {
        formData.append('attachments', f);
      }

      const res = await axios.post(apiUrl('/api/tickets'), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      void res;
      setMessage('Ticket created.');
      setSubject('');
      setDescription('');
      setPriority('MEDIUM');
      setTopicId('');
      setSlaLevels([]);
      setSlaLevelId('');
      setAttachments([]);
      setPage(1);
      await fetchTickets({ pageOverride: 1 });
      await fetchLimits();
    } catch (e2) {
      const code = e2?.response?.data?.code;
      if (code === 'SUPPORT_TICKET_LIMIT_REACHED' || code === 'ENHANCEMENT_LIMIT_REACHED') {
        setError(kind === 'SUPPORT' ? 'Reached monthly support ticket limit' : 'Reached once-off enhancement limit');
      } else {
        setError(e2?.response?.data?.error || e2?.message || 'Failed to create ticket');
      }
      setPage(1);
      await fetchTickets({ pageOverride: 1 });
      await fetchLimits();
    } finally {
      setCreating(false);
    }
  };

  const renderLimitCard = () => null;

  return (
    <div className="w-full">
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Support</h1>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">Create a support ticket or request a once-off custom enhancement.</p>
      </div>

      {renderLimitsCards()}

      {message && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-700 text-green-800 dark:text-green-300 text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-700 text-red-800 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
        {/* Create Ticket card */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1)' }} />
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Create Ticket</h2>
          </div>
          <div className="p-5">
          <form onSubmit={onCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Type</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="w-full"
            >
              <option value="SUPPORT">Support Ticket</option>
              <option value="ENHANCEMENT">Once-off Custom Enhancement</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Topic</label>
            <select
              value={topicId}
              onChange={(e) => setTopicId(e.target.value)}
              className="w-full"
            >
              <option value="">Select topic…</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <div className="text-xs text-gray-400 dark:text-slate-500 mt-1">Priority is decided by the SLA for the topic.</div>
          </div>

          <div className="p-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
            <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">SLA (auto)</div>
            <div className="text-sm text-gray-700 dark:text-slate-300 mt-1.5">
              {(() => {
                const match = slaLevels.find((x) => String(x.id) === String(slaLevelId));
                if (!topicId) return 'Select a topic to see SLA.';
                if (!match) return 'No SLA configured for this topic.';
                return `${match.name} (Priority: ${String(match.priority || 'MEDIUM').toUpperCase()})`;
              })()}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary"
              className={`w-full ${subject.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`min-h-28 w-full ${description.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
              placeholder="Describe the issue / enhancement request"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Attachments (optional)</label>
            <input
              type="file"
              multiple
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, 5);
                setAttachments(files);
              }}
            />
            {attachments.length > 0 && (
              <div className="mt-2 text-sm text-gray-700 dark:text-slate-300">
                <div className="font-semibold">Selected:</div>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {attachments.map((f, idx) => (
                    <li key={`${f.name}-${idx}`}>{f.name} ({formatBytes(f.size)})</li>
                  ))}
                </ul>
                <div className="text-xs text-gray-400 dark:text-slate-500 mt-1">Max 5 files, 10MB each.</div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button
              type="submit"
              variant="primary"
              loading={creating}
              busyLabel="Creating…"
              disabled={creating || !canCreate()}
              title={!canCreate() ? (kind === 'SUPPORT' ? 'Reached monthly support ticket limit' : 'Reached once-off enhancement limit') : 'Create'}
            >
              {creating ? 'Creating…' : (!canCreate() ? 'Limit reached' : 'Create')}
            </Button>
            <Button type="button" variant="secondary" onClick={fetchAll} disabled={loading}>
              Refresh
            </Button>
          </div>
          </form>
          </div>
        </div>

        {/* My Tickets card */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #10b981, #3b82f6)' }} />
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-slate-100">My Tickets</h2>
            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" onClick={exportPdf} disabled={loading}>
                Export PDF
              </Button>
              <Button type="button" variant="secondary" onClick={fetchAll} disabled={loading}>Refresh</Button>
            </div>
          </div>
          <div className="p-5">

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject/description…"
              className="toolbar-control w-full"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="toolbar-control sm:w-56 w-full"
              title="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="OPEN">Open</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="CLOSED">Closed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="w-full sm:w-56">
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Date from</label>
              <input
                type="date"
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
                max={todayMax}
                className="toolbar-control w-full"
              />
            </div>
            <div className="w-full sm:w-56">
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Date to</label>
              <input
                type="date"
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
                max={todayMax}
                className="toolbar-control w-full"
              />
            </div>
            <div className="flex-1" />
          </div>

          <div className="text-xs text-gray-400 dark:text-slate-500 mb-2">Showing {tickets.length} of {ticketsTotal} tickets</div>
          {(reportDateFrom || reportDateTo) && (
            <div className="text-xs text-gray-400 dark:text-slate-500 mb-2">
              Date range: {reportDateFrom || '…'} → {reportDateTo || '…'}
            </div>
          )}

          <Pager page={page} pages={totalPages} onPage={setPage} disabled={loading} className="mb-4" />

          {loading ? (
            <div className="flex items-center gap-2 py-6 justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600"></div>
              <span className="text-sm text-gray-500 dark:text-slate-400">Loading…</span>
            </div>
          ) : tickets.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400 dark:text-slate-500">No tickets yet.</div>
          ) : (
            <div className="space-y-3">
              {tickets.map(t => {
                const hasUnread = !!t.has_unread_support_messages;
                const hasUnreadStatus = !!t.has_unread_status_change;
                const showUnreadAny = hasUnread || hasUnreadStatus;
                return (
                  <div
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectTicket(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onSelectTicket(t);
                    }}
                    className={`relative w-full p-3.5 rounded-xl border cursor-pointer transition-all duration-150 ${
                      showUnreadAny
                        ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
                        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-blue-200 dark:hover:border-slate-500 hover:shadow-sm'
                    }`}
                  >
                    {showUnreadAny && (
                      <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-r from-amber-200/20 via-transparent to-transparent" />
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 dark:text-slate-100 truncate">{t.subject}</div>
                        {(hasUnread || hasUnreadStatus) && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            {hasUnread && (
                              <div className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 dark:bg-amber-700/40 dark:text-amber-300">
                                New support reply
                              </div>
                            )}
                            {hasUnreadStatus && (
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                                  <Bell size={12} /> Status changed
                                </span>
                                <span className="text-xs text-blue-800 dark:text-blue-400">
                                  {String(t.last_status_change_from || '').toUpperCase()} → {String(t.last_status_change_to || t.status || '').toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusBadge status={t.status || 'OPEN'} label={String(t.status || 'OPEN').replace(/_/g, ' ')} />
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onResendEmailForTicket(t?.id);
                          }}
                          disabled={Number(resendingTicketId) === Number(t?.id)}
                          title="Resend the support link email (cc you)"
                        >
                          {Number(resendingTicketId) === Number(t?.id) ? 'Resending…' : 'Resend support link'}
                        </Button>
                      </div>
                    </div>

                    <div className="mt-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        <InfoChip label="#" value={String(t.id)} />
                        <InfoChip label="Type" value={String(t.kind || '').toUpperCase()} tone="blue" />
                        <InfoChip label="Created" value={t.created_at ? formatDateTimeDMY(t.created_at) : ''} />
                        <InfoChip label="By" value={t.created_by_email || ''} tone="blue" />
                        <InfoChip label="Priority" value={t.priority ? String(t.priority).toUpperCase() : ''} tone={priorityTone(t.priority)} />
                        <InfoChip label="Topic" value={t.topic_name || ''} tone="purple" />
                        <InfoChip label="SLA" value={t.sla_level_name || ''} />
                        {(() => {
                          const s = String(t.status || '').toUpperCase();
                          const isClosed = s === 'CLOSED' || s === 'CANCELLED';
                          const dueAt = t.response_due_at;
                          const overdue = !!dueAt && !t.first_response_at && !isClosed && (new Date(dueAt).getTime() < Date.now());
                          const chip = slaChip({ dueAt, violatedAt: t.response_sla_violated_at, overdue });
                          return chip ? <InfoChip label="Resp SLA" value={chip.value} tone={chip.tone} /> : null;
                        })()}
                        {(() => {
                          const s = String(t.status || '').toUpperCase();
                          const isClosed = s === 'CLOSED' || s === 'CANCELLED';
                          const dueAt = t.resolution_due_at;
                          const overdue = !!dueAt && !isClosed && (new Date(dueAt).getTime() < Date.now());
                          const chip = slaChip({ dueAt, violatedAt: t.resolution_sla_violated_at, overdue });
                          return chip ? <InfoChip label="Res SLA" value={chip.value} tone={chip.tone} /> : null;
                        })()}
                        <InfoChip
                          label="Assigned"
                          value={t.assigned_technician_name ? `${t.assigned_technician_name}${t.assigned_technician_email ? ` (${t.assigned_technician_email})` : ''}` : ''}
                          tone="green"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Pager page={page} pages={totalPages} onPage={setPage} disabled={loading} className="mt-4" />
          </div>{/* end p-5 */}
        </div>{/* end My Tickets card */}
      </div>{/* end grid */}

      {isModalOpen && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeModal}
            aria-label="Close"
          />

          <div className="relative w-full max-w-3xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1)' }} />
            <div className="p-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-gray-500 dark:text-slate-400">Ticket #{selectedTicket.id} · {selectedTicket.kind}</div>
                <div className="text-base font-bold text-gray-900 dark:text-slate-100 truncate">{selectedTicket.subject}</div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div><DisplayField label="Created">{selectedTicket.created_at ? formatDateTimeDMY(selectedTicket.created_at) : ''}</DisplayField></div>
                  <div><DisplayField label="By" tone="blue">{selectedTicket.created_by_email || ''}</DisplayField></div>
                  <div><DisplayField label="Priority" tone={priorityTone(selectedTicket.priority)}>{selectedTicket.priority ? String(selectedTicket.priority).toUpperCase() : ''}</DisplayField></div>
                  <div><DisplayField label="Topic" tone="purple">{selectedTicket.topic_name || ''}</DisplayField></div>
                  <div><DisplayField label="SLA">{selectedTicket.sla_level_name || ''}</DisplayField></div>
                  <div>
                    {(() => {
                      const s = String(selectedTicket.status || '').toUpperCase();
                      const isClosed = s === 'CLOSED' || s === 'CANCELLED';
                      const dueAt = selectedTicket.response_due_at;
                      const overdue = !!dueAt && !selectedTicket.first_response_at && !isClosed && (new Date(dueAt).getTime() < Date.now());
                      const chip = slaChip({ dueAt, violatedAt: selectedTicket.response_sla_violated_at, overdue });
                      return chip ? <DisplayField label="Resp SLA" tone={chip.tone}>{chip.value}</DisplayField> : <DisplayField label="Resp SLA">{null}</DisplayField>;
                    })()}
                  </div>
                  <div>
                    {(() => {
                      const s = String(selectedTicket.status || '').toUpperCase();
                      const isClosed = s === 'CLOSED' || s === 'CANCELLED';
                      const dueAt = selectedTicket.resolution_due_at;
                      const overdue = !!dueAt && !isClosed && (new Date(dueAt).getTime() < Date.now());
                      const chip = slaChip({ dueAt, violatedAt: selectedTicket.resolution_sla_violated_at, overdue });
                      return chip ? <DisplayField label="Res SLA" tone={chip.tone}>{chip.value}</DisplayField> : <DisplayField label="Res SLA">{null}</DisplayField>;
                    })()}
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3">
                    <DisplayField label="Assigned" tone="green">{selectedTicket.assigned_technician_name ? `${selectedTicket.assigned_technician_name}${selectedTicket.assigned_technician_email ? ` (${selectedTicket.assigned_technician_email})` : ''}` : ''}</DisplayField>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <StatusBadge status={selectedTicket.status || 'OPEN'} label={String(selectedTicket.status || 'OPEN').replace(/_/g, ' ')} />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => openResendModal(selectedTicket?.id)}
                  disabled={Number(resendingTicketId) === Number(selectedTicket?.id)}
                >
                  {Number(resendingTicketId) === Number(selectedTicket?.id) ? 'Resending…' : 'Resend support link'}
                </Button>
                <Button type="button" variant="secondary" onClick={closeModal}>Close</Button>
              </div>
            </div>

            <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">Description</div>
                <div className="whitespace-pre-wrap text-gray-800 dark:text-slate-200 border dark:border-slate-700 rounded-lg p-3 bg-gray-50 dark:bg-slate-700">{selectedTicket.description}</div>
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">Attachments</div>
                {attachmentsLoading ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">Loading attachments…</div>
                ) : ticketAttachments.length === 0 ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">No attachments.</div>
                ) : (
                  <div className="space-y-2">
                    {ticketAttachments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-2 border dark:border-slate-700 rounded-lg p-2 bg-gray-50 dark:bg-slate-700">
                        <div className="text-sm text-gray-800 dark:text-slate-200 truncate">{a.filename}</div>
                        <ActionButton
                          type="button"
                          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 whitespace-nowrap"
                          onClick={() => viewAttachment(a.id, a.filename)}
                        >
                          View
                        </ActionButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Comments</div>
                {messagesLoading ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">Loading comments…</div>
                ) : ticketMessages.length === 0 ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">No comments yet.</div>
                ) : (
                  <div className="space-y-2">
                    {ticketMessages.map((m) => (
                      <div key={m.id} className="border dark:border-slate-700 rounded-lg p-2 dark:bg-slate-800">
                        <div className="text-xs text-gray-500 dark:text-slate-400">
                          {m.author_name || 'User'}{m.author_email ? ` (${m.author_email})` : ''} · {m.created_at ? formatDateTimeDMY(m.created_at) : '—'}
                        </div>
                        <div className="text-sm text-gray-800 dark:text-slate-200 whitespace-pre-wrap">{m.message}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="min-h-20 w-full"
                    placeholder="Add a comment…"
                    disabled={postingComment}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={onPostComment}
                      disabled={postingComment || !String(newComment || '').trim()}
                    >
                      {postingComment ? 'Posting…' : 'Post comment'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => loadMessages(selectedTicket.id)} disabled={messagesLoading}>
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Status change history</div>
                {statusHistoryLoading ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">Loading status history…</div>
                ) : statusHistory.length === 0 ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">No status changes yet.</div>
                ) : (
                  <div className="space-y-2">
                    {statusHistory.map((h) => (
                      <div key={h.id} className="border dark:border-slate-700 rounded-lg p-2 bg-gray-50 dark:bg-slate-700">
                        <div className="text-xs text-gray-500 dark:text-slate-400">
                          {h.changed_at ? formatDateTimeDMY(h.changed_at) : '—'}
                        </div>
                        <div className="text-sm text-gray-800 dark:text-slate-200">
                          {String(h.from_status || '').toUpperCase()} → {String(h.to_status || '').toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showResendModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setShowResendModal(false);
              setResendTicketId(null);
              setResendTechnicianId('');
            }}
            aria-label="Close"
          />

          <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-xl shadow-xl border dark:border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg font-bold text-gray-900 dark:text-slate-100">Resend support link</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Choose which technician to send this ticket to.</div>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowResendModal(false);
                  setResendTicketId(null);
                  setResendTechnicianId('');
                }}
              >
                Close
              </Button>
            </div>

            <div className="p-4 space-y-3">
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Technician</label>
              {techniciansLoading && (
                <div className="text-sm text-gray-600 dark:text-slate-400">Loading technicians…</div>
              )}
              {!techniciansLoading && techniciansLoadError && (
                <div className="text-rose-500 text-xs mt-1">{techniciansLoadError}</div>
              )}
              <select
                value={resendTechnicianId}
                onChange={(e) => setResendTechnicianId(e.target.value)}
                className={`w-full ${resendTechnicianId ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                disabled={techniciansLoading}
              >
                <option value="">Select technician…</option>
                {technicians.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || 'Technician'}{t.email ? ` (${t.email})` : ''}
                  </option>
                ))}
              </select>

              {!techniciansLoading && !techniciansLoadError && technicians.length === 0 && (
                <div className="text-sm text-amber-700 dark:text-amber-400">
                  No technicians configured to receive tickets. Add at least one active technician with "receives tickets" enabled.
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={onConfirmResend}
                  disabled={Number(resendingTicketId) === Number(resendTicketId) || !resendTechnicianId}
                >
                  {Number(resendingTicketId) === Number(resendTicketId) ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
